import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import Ellipsis from '@wix/da-ds/pkg/Icons/Ellipsis';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import IconButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import { useWatchButton } from '@wix/da-shared-react/pkg/WatchButton/useWatchButton';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';

export interface Props {
  group: PapiUser;
  className?: string;
  requestLeave: (group: PapiUser) => void;
}

const GroupMenu: React.FC<Props> = ({ group, className, requestLeave }) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const {
    isWatching,
    biData: watchBiData,
    onClick,
  } = useWatchButton(group, group.isWatching);
  const ncNew = useFlag('nc_new');

  const dropdownItems = useMemo(() => {
    const itemBiData = {
      evid: BiEventType.WATCH_MENU_ITEM_SELECTION,
      itemid: group.userId,
      typeid: ResourceType.GROUP,
    };
    return [
      isMobile && {
        label: t('subnavigation.button.groupDropdown.items.visitGroup'),
        link: Url.userLink(group.username),
        biData: {
          itemid: group.userId,
          typeid: ResourceType.GROUP,
          evid: BiEventType.USERNAME_CLICK,
        },
      },
      isMobile &&
        group.extendedGroup?.isAdmin && {
          label: t('subnavigation.button.groupDropdown.items.notifications'),
          link: Url.groupNotificationsLink(ncNew, group.userId),
          biData: {
            ...itemBiData,
            menu_item: 'visitNotifications',
          },
        },
      {
        label: t('subnavigation.button.groupDropdown.items.messageAdmins'),
        link: Url.userNoteLink(group.username),
        biData: {
          ...itemBiData,
          menu_item: 'messageAdmins',
        },
      },
      {
        label: isWatching
          ? t('subnavigation.button.groupDropdown.items.unwatch')
          : t('subnavigation.button.groupDropdown.items.watch'),
        biData: watchBiData,
        onClick,
      },
      group.extendedGroup?.isIngroup && {
        label: t('subnavigation.button.groupDropdown.items.leaveGroup'),
        onClick: () => requestLeave(group),
        biData: {
          ...itemBiData,
          menu_item: 'leaveGroup',
        },
      },
    ];
  }, [
    group,
    watchBiData,
    isWatching,
    onClick,
    requestLeave,
    t,
    isMobile,
    ncNew,
  ]);

  return (
    <IconButtonWithContextMenu
      items={dropdownItems}
      className={className}
      onOpenBiData={{
        evid: BiEventType.MORE_MENU_CLICK,
        typeid: ResourceType.GROUP,
        itemid: group.userId,
      }}
      withButton
    >
      <Ellipsis size={IconSize.MEDIUM} />
    </IconButtonWithContextMenu>
  );
};
GroupMenu.displayName = 'GroupMenu';

export default GroupMenu;
